import styled from '@emotion/styled';
import { css, SerializedStyles, Theme } from '@emotion/react';

import { ContentModuleStyle } from '@silvertours/common-landingpages-view';
import { Global } from '../../styles';

const getBackground = (
  theme: Theme,
  style: ContentModuleStyle,
): SerializedStyles | string =>
  ({
    default: ';',
    gray: css`
      background-color: ${theme.colors.neutral.grey20};
      padding-top: 6rem;
      padding-bottom: 6rem;
    `,
    green: css`
      background-image: ${theme.linearGradient(theme.gradient.green)};
    `,
    greenAlt: css`
      background-color: ${theme.colors.brand.green40};
    `,
    mint: css`
      background-image: ${theme.linearGradient(theme.gradient.mint)};
    `,
    mintAlt: css`
      background-image: ${theme.linearGradient(
        theme.gradient.mintAlt,
        '90deg',
      )};
    `,
    orange: css`
      background-color: ${theme.colors.brand.orange40};
    `,
    orangeAlt: css`
      background-color: ${theme.colors.brand.orange40};
    `,
    brand: css`
      background-image: ${theme.linearGradient(theme.gradient.brand, '135deg')};
    `,
  })[style];

const WaveSvg = styled.svg`
  width: 100%;
  height: auto;
  fill: ${({ theme }) => theme.colors.neutral.white100};
`;

const FixedWave = styled.div`
  position: relative;
`;

const FixedWaveContent = styled.div`
  position: relative;
  z-index: 10;
`;

const FixedWaveWaves = styled.div<{
  height?: number;
  isFullWidth?: boolean;
  backgroundStyle?: ContentModuleStyle;
}>`
  ${Global.layoutContainerContent};

  ${({ theme, backgroundStyle }) =>
    backgroundStyle && getBackground(theme, backgroundStyle)};

  position: absolute;
  top: 0;
  left: ${({ isFullWidth }) => (isFullWidth ? 'calc(-50vw + 50%)' : '0')};
  right: ${({ isFullWidth }) => (isFullWidth ? 'calc(-50vw + 50%)' : '')};
  z-index: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: ${({ isFullWidth }) => (isFullWidth ? '' : '100%')};
  height: ${({ height }) => (height ? `${height}px` : '')};

  ${({ theme }) => theme.mq('xxl')} {
    left: ${({ isFullWidth }) => (isFullWidth ? 'calc(-960px + 50%)' : '0')};
    right: ${({ isFullWidth }) => (isFullWidth ? 'calc(-960px + 50%)' : '')};
  }

  /* Fix for sub-pixel hairline showing on some systems due to scaled SVG. */
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.neutral.white100};
  }
`;

const FixedWaveEnd = styled.div`
  & > svg {
    margin-bottom: -6px;
  }
`;

const ScalableWaveSvg = css`
  & > svg {
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
    margin-top: -1px;
  }
`;

/** Get spacing for scalable waves, with `scalable6` being a special ”unicorn“. */
const getSpacing = (
  waveType: string,
  regularValue: string,
  specialValue: string,
) => (waveType === 'scalable6' ? specialValue : regularValue);

const ScalableWave = styled.div<{
  waveType: string;
  isFullWidth?: boolean;
  backgroundStyle?: ContentModuleStyle;
  extendBottom?: boolean;
}>`
  ${Global.layoutContainerContent};

  ${({ theme, backgroundStyle, extendBottom }) =>
    !extendBottom && backgroundStyle && getBackground(theme, backgroundStyle)};

  ${({ extendBottom, theme, backgroundStyle }) =>
    extendBottom &&
    css`
      position: relative;
      padding-bottom: 6rem;
      margin-bottom: -8rem;

      ${theme.mq('md')} {
        padding-bottom: 8rem;
        margin-bottom: -8rem;
      }

      ${theme.mq('lg')} {
        padding-bottom: 10rem;
        margin-bottom: -8rem;
      }

      ${theme.mq('xl')} {
        padding-bottom: 12rem;
        margin-bottom: -8rem;
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0 0 13rem;
        z-index: 0;
        ${backgroundStyle && getBackground(theme, backgroundStyle)};

        ${theme.mq('md')} {
          bottom: 15rem;
        }

        ${theme.mq('lg')} {
          bottom: 10rem;
        }

        ${theme.mq('xl')} {
          bottom: 12rem;
        }
      }

      & > :first-of-type {
        position: relative;
        z-index: 1;
      }

      & > :last-child {
        position: relative;
        z-index: 0;
      }

      & > :is(:not(:first-of-type, :last-child)) {
        position: relative;
        z-index: 2;
      }
    `}

  ${({ backgroundStyle, theme, waveType, isFullWidth, extendBottom }) => {
    if (!isFullWidth) return '';

    return css`
      margin-top: ${getSpacing(waveType, '-3rem', '-1.25rem')};
      overflow: hidden;

      ${theme.mq('md')} {
        margin-top: ${getSpacing(waveType, '-6rem', '-1.25rem')};
        ${
          ['mint', 'mintAlt'].includes(backgroundStyle || '') &&
          'padding-bottom: 1rem;'
        }
      }
      ${theme.mq('lg')} {
        margin-top: ${getSpacing(waveType, '-3rem', '-1.25rem')};
        ${
          ['mint', 'mintAlt'].includes(backgroundStyle || '') &&
          'padding-bottom: 0;'
        }
      }
      ${theme.mq('xl')} {
        margin-top: ${
          extendBottom
            ? getSpacing(waveType, '-7rem', '-1.25rem')
            : getSpacing(waveType, '-6rem', '-1.25rem')
        };
      }
      ${ScalableWaveSvg}
    `;
  }};
`;

const ScalableWaveEnd = styled.div<{
  waveType: string;
  isFullWidth?: boolean;
  extendBottom?: boolean;
}>`
  margin-bottom: ${({ waveType, extendBottom }) =>
    extendBottom
      ? getSpacing(waveType, '-0.5rem', '-1.75rem')
      : getSpacing(waveType, '-2rem', '-1.75rem')};

  ${({ extendBottom, theme }) =>
    extendBottom &&
    css`
      transform: translateY(-7rem);

      ${theme.mq('md')} {
        transform: translateY(-4rem);
      }

      ${theme.mq('lg')} {
        transform: translateY(0);
      }

      ${theme.mq('xl')} {
        transform: translateY(0);
      }
    `}

  ${({ theme }) => theme.mq('md')} {
    margin-bottom: ${({ waveType, extendBottom }) =>
      extendBottom
        ? getSpacing(waveType, '-0.75rem', '-2.25rem')
        : getSpacing(waveType, '-3.5rem', '-2.25rem')};
  }

  ${({ theme }) => theme.mq('lg')} {
    margin-bottom: ${({ waveType }) =>
      getSpacing(waveType, '-1.5rem', '-0.75rem')};
  }

  ${({ theme }) => theme.mq('xl')} {
    margin-bottom: ${({ waveType }) =>
      getSpacing(waveType, '-2rem', '-0.75rem')};
  }

  ${({ isFullWidth }) => isFullWidth && ScalableWaveSvg};
`;

export {
  WaveSvg,
  FixedWave,
  FixedWaveContent,
  FixedWaveWaves,
  FixedWaveEnd,
  ScalableWave,
  ScalableWaveEnd,
};
